import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  pullCampaignOffices,
  pullCampaignScripts,
  pullCampaignSurveys,
  updateOrCreateSurvey,
} from '../../store/api/calls';
import { Survey } from '@/types/api';

import NavBaseTemplate from '../templates/NavBaseTemplate';
import BaseForm from '../theme/forms/BaseForm';
import DataTable from '../theme/tables/DataTable';
import BaseModal from '../theme/modals/BaseModal';
import FeedbackTile from '../theme/modals/FeedbackTile';
import Moment from 'moment';

interface Office {
  id: number;
  name: string;
}

interface Script {
  id: number;
  name: string;
}

interface FormInput {
  type: string;
  title: string;
  dataKey: string;
  placeholder?: string;
  options?: { value: number | null; display: string }[];
  value?: any;
  validator?: (val: any) => boolean;
  errorMsg?: string;
}

interface RootState {
  session: { focusedCampaign: { id: number } };
  surveys: { data: Survey[]; loading: boolean; error: boolean };
  offices: { data: Office[]; loading: boolean };
  scripts: { data: Script[]; loading: boolean };
}

const mapStateToProps = (state: RootState) => ({
  focusedCampaign: state.session.focusedCampaign,
  surveys: state.surveys.data,
  loading: state.surveys.loading,
  error: state.surveys.error,
  campaignOffices: state.offices.data,
  campaignScripts: state.scripts.data,
  officesLoading: state.offices.loading,
  scriptsLoading: state.scripts.loading,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface SurveysState {
  editModalOpen: boolean;
  deleteModalOpen: boolean;
  editingInstance: Partial<Survey>;
  formInputs: FormInput[];
}

class SurveysScreen extends React.Component<PropsFromRedux, SurveysState> {
  constructor(props: PropsFromRedux) {
    super(props);

    this.state = {
      editModalOpen: false,
      deleteModalOpen: false,
      editingInstance: {},
      formInputs: [],
    };
  }

  componentDidMount() {
    this.handleMountOrCampaignUpdate();
  }

  componentDidUpdate(prevProps: PropsFromRedux) {
    if (this.props.focusedCampaign.id !== prevProps.focusedCampaign.id) {
      this.handleMountOrCampaignUpdate();
    }

    const formDepsChanged =
      this.props.campaignOffices !== prevProps.campaignOffices ||
      this.props.campaignScripts !== prevProps.campaignScripts;

    if (this.state.editModalOpen && formDepsChanged) {
      this.setState({
        formInputs: this._generateFormInputs(this.state.editingInstance),
      });
    }
  }

  handleMountOrCampaignUpdate = () => {
    pullCampaignSurveys();
    pullCampaignOffices();
    pullCampaignScripts();
  };

  _generateFormInputs(modelInstance: Partial<Survey> = {}): FormInput[] {
    const offices: { value: number | null; display: string }[] = [{ value: null, display: '' }];
    this.props.campaignOffices.forEach((d) => {
      offices.push({ value: d.id, display: d.name });
    });

    const scripts: { value: number | null; display: string }[] = [{ value: null, display: '' }];
    this.props.campaignScripts.forEach((d) => {
      scripts.push({ value: d.id, display: d.name });
    });

    return [
      {
        type: 'text',
        title: 'Name',
        dataKey: 'name',
        placeholder: 'Use something descriptive',
        value: modelInstance.name,
        validator: (val: any) => !!val && String(val).trim().length > 0,
        errorMsg: 'Name is required.',
      },
      {
        type: 'select',
        title: 'Office',
        dataKey: 'office',
        options: offices,
        value: modelInstance.office,
      },
      {
        type: 'select',
        title: 'Script',
        dataKey: 'script',
        options: scripts,
        value: modelInstance.script,
        validator: (val: any) => !!val && val !== '',
        errorMsg: 'Please select a script.',
      },
    ];
  }

  isFormDependenciesLoading = () => {
    return this.props.officesLoading || this.props.scriptsLoading;
  };

  /* -----------------------------
   * Table Data
   * ----------------------------- */

  _filterKeysForDataTable() {
    return this.props.surveys.map((d) => {
      const office = this.props.campaignOffices.find((o) => o.id === d.office) || { name: '' };
      const script = this.props.campaignScripts.find((s) => s.id === d.script) || { name: '' };

      return {
        name: d.name,
        office: office.name,
        script: script.name,
        created_on: Moment(d.created_at).format('MM/DD/YY'),
        _created_on: Moment(d.created_at),

        // RIGHT-MOST ACTION COLUMN — NO HEADER
        '\u200B': (
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
            <button
              className="et-datatable-button button button-outline"
              onClick={() => this.onOpenEditModal(d)}
            >
              Edit
            </button>
            <button
              className="et-datatable-button et-datatable-button-red button button-outline"
              onClick={() => this.onOpenDeleteModal(d)}
            >
              Remove
            </button>
          </div>
        ),

        _data: d,
      };
    });
  }

  /* -----------------------------
   * Edit / Delete
   * ----------------------------- */

  onOpenEditModal = (modelInstance: Partial<Survey> = {}) => {
    this.setState({
      editModalOpen: true,
      editingInstance: modelInstance,
      formInputs: this._generateFormInputs(modelInstance),
    });
  };

  onCloseEditModal = () => {
    this.setState({ editModalOpen: false, editingInstance: {} });
  };

  onOpenDeleteModal = (modelInstance: Partial<Survey> = {}) => {
    this.setState({ deleteModalOpen: true, editingInstance: modelInstance });
  };

  onCloseDeleteModal = () => {
    this.setState({ deleteModalOpen: false, editingInstance: {} });
  };

  handleUpdateCreateSubmission = (data: Record<string, any>) => {
    const id = this.state.editingInstance.id;
    const campaign = this.props.focusedCampaign.id;

    if (id) data = { ...data, id };
    data = { ...data, campaign };

    if (data.office === '' || data.office === undefined) {
      data.office = null;
    }

    updateOrCreateSurvey(data).then(this.onCloseEditModal);
  };

  handleRemoveSubmission = () => {
    const id = this.state.editingInstance.id;
    updateOrCreateSurvey({ id, is_active: false }).then(this.onCloseDeleteModal);
  };

  /* -----------------------------
   * Render
   * ----------------------------- */

  render() {
    return (
      <NavBaseTemplate>
        <div className="button-table-top-wrapper">
          <a className="button" onClick={() => this.onOpenEditModal({})}>
            + Create Survey
          </a>
        </div>

        <DataTable data={this._filterKeysForDataTable() || []} title="Surveys" />

        <FeedbackTile
          text={
            <div>
              <i className="fas fa-asterisk fa-spin" style={{ marginRight: '15px' }} />
              Loading...
            </div>
          }
          visible={this.props.loading}
        />

        {/* EDIT MODAL */}
        <BaseModal
          open={this.state.editModalOpen}
          onClose={this.onCloseEditModal}
          center
          loading={this.state.editModalOpen && this.isFormDependenciesLoading()}
        >
          <BaseForm
            inputs={this.state.formInputs}
            onCancel={this.onCloseEditModal}
            onSubmit={this.handleUpdateCreateSubmission}
          />
        </BaseModal>

        {/* DELETE MODAL */}
        <BaseModal open={this.state.deleteModalOpen} onClose={this.onCloseDeleteModal} center>
          <div className="modal-internal-wrapper">
            <p>
              Are you sure you want to remove the survey{' '}
              <b>{this.state.editingInstance.name}</b>?
            </p>
            <div className="button-wrapper">
              <div className="button" onClick={this.handleRemoveSubmission}>
                Remove
              </div>
              <div className="button button-outline" onClick={this.onCloseDeleteModal}>
                Cancel
              </div>
            </div>
          </div>
        </BaseModal>
      </NavBaseTemplate>
    );
  }
}

export default connector(SurveysScreen);
